import type { ReactNode } from "react";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { Logo } from "@/components/app/logo";
import { Card } from "@/components/ui/card";
import { hasAccounts, publicDemo } from "@/lib/account";
import { matchPath, useLocation } from "@/lib/pages";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { DemoRequestPage } from "./demo";
import { InvitePage } from "./invite";
import { ProfilePage, RestrictedPage } from "./profile";
import { ForgotPage, ResetPage, SignInPage } from "./sign-in";
import { WelcomePage } from "./welcome";

/**
 * /account/…: signing in, password reset, invitations, the welcome steps, the profile and the
 * restricted state (ADR 0011), and the public demo's request page (ADR 0012). Only with accounts; elsewhere the
 * address leads back to the garages.
 */
export function AccountApp() {
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const path = pathname.replace(/^\/account/, "") || "/";

  useEffect(() => {
    if (!hasAccounts()) window.location.replace("/");
  }, []);
  if (!hasAccounts()) return null;

  const invite = matchPath("/invite/:token", path);
  const reset = matchPath("/reset/:token", path);
  let page: ReactNode;
  if (invite) page = <InvitePage token={invite.token ?? ""} />;
  else if (reset) page = <ResetPage token={reset.token ?? ""} />;
  else if (path === "/forgot") page = <ForgotPage />;
  else if (path === "/profile") page = <ProfilePage />;
  else if (path === "/restricted") page = <RestrictedPage />;
  else if (path === "/welcome") page = <WelcomePage />;
  else if (path === "/demo" && publicDemo()) page = <DemoRequestPage />;
  else page = <SignInPage />;

  return (
    <>
      {page}
      <Toaster
        theme={theme}
        position="bottom-center"
        toastOptions={{
          classNames: {
            toast: "!rounded-xl !border-border !bg-surface-raised !text-foreground !shadow-lg",
          },
        }}
      />
    </>
  );
}

/** A centered card with the logo above it: the sign-in, reset and invitation pages. */
export function AuthCard({
  title,
  body,
  children,
  wide = false,
}: {
  title: ReactNode;
  body?: ReactNode;
  children?: ReactNode;
  wide?: boolean;
}) {
  return (
    <main className="flex min-h-dvh flex-col items-center bg-background px-4 pt-[max(3rem,env(safe-area-inset-top))] pb-12 sm:justify-center sm:pt-12">
      <a
        href="/"
        className="mb-8 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <Logo />
      </a>
      <Card className={cn("w-full p-5 sm:p-6", wide ? "max-w-lg" : "max-w-sm")}>
        <h1 className="text-xl font-semibold tracking-tight text-balance">{title}</h1>
        {body ? (
          <div className="mt-1.5 text-sm text-pretty text-muted-foreground">{body}</div>
        ) : null}
        {children ? <div className="mt-5">{children}</div> : null}
      </Card>
    </main>
  );
}
